import { Search, X } from 'lucide-react';
import { Input, Select } from '@/components/ui/input';
import { EntityPicker } from '@/components/forms/pickers';
import { entityKindOf } from '@/lib/entities';
import { useI18n } from '@/lib/i18n';
import { deref, queryParams, type Parameter } from '@/lib/openapi';
import type { Query } from '@/lib/api';
import { Button } from '@/components/ui/button';
import * as fmt from '@/lib/format';

const SKIP = new Set(['limit', 'cursor']);

/** Filter bar generated from a list endpoint's query parameters. */
export function FiltersBar({ path, value, onChange, context }: { path: string; value: Query; onChange(q: Query): void; context: string }) {
  const { label, t } = useI18n();
  const params = queryParams(path).filter((p) => !SKIP.has(p.name));
  if (!params.length) return null;
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v === '' || v === null ? undefined : (v as string) });
  const active = params.filter((p) => !p.required && value[p.name] !== undefined && value[p.name] !== '').length;
  const search = params.find((p) => p.name === 'search');
  const rest = params.filter((p) => p !== search);
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      {search && (
        <div className="w-full sm:w-72">
          <Input leading={<Search />} placeholder={`${t('Search')}…`} value={(value.search as string) ?? ''} onChange={(e) => set('search', e.target.value)} />
        </div>
      )}
      {rest.map((p) => (
        <div key={p.name} className="w-full sm:w-48">
          <div className="mb-1 text-[11.5px] font-medium uppercase tracking-wide text-muted-foreground">
            {label(p.name)}
            {p.required && ' *'}
          </div>
          <FilterControl p={p} value={value[p.name]} onChange={(v) => set(p.name, v)} context={context} />
        </div>
      ))}
      {active > 0 && (
        <Button variant="ghost" size="sm" onClick={() => onChange(Object.fromEntries(params.filter((p) => p.required).map((p) => [p.name, value[p.name]])))}>
          <X /> {t('Clear')} ({active})
        </Button>
      )}
    </div>
  );
}

function FilterControl({ p, value, onChange, context }: { p: Parameter; value: unknown; onChange(v: unknown): void; context: string }) {
  const s = deref(p.schema);
  const kind = s.format === 'uuid' || /Id$/.test(p.name) ? entityKindOf(p.name, context) : null;
  if (kind) return <EntityPicker kind={kind} value={(value as string) ?? null} onChange={(v) => onChange(v)} placeholder="Any" />;
  if (s.enum) {
    return (
      <Select value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {s.enum.map((o) => (
          <option key={String(o)} value={String(o)}>
            {String(o).replace(/_/g, ' ')}
          </option>
        ))}
      </Select>
    );
  }
  if (s.type === 'boolean') {
    return (
      <Select value={value === undefined ? '' : String(value)} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Select>
    );
  }
  if (/^(from|to|date)$|At$/.test(p.name)) {
    return <Input type="date" value={typeof value === 'string' ? value.slice(0, 10) : ''} onChange={(e) => onChange(e.target.value ? (p.name === 'date' ? e.target.value : new Date(`${e.target.value}T${p.name === 'to' ? '23:59:59' : '00:00:00'}`).toISOString()) : '')} />;
  }
  if (s.type === 'number') return <Input type="number" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
  return <Input value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;
}

/** Defaults for required filters (date windows) so lists load immediately. */
export function defaultQuery(path: string): Query {
  const out: Query = {};
  for (const p of queryParams(path)) {
    if (!p.required) continue;
    if (p.name === 'from') out.from = new Date(Date.now() - 30 * 86_400_000).toISOString();
    else if (p.name === 'to') out.to = new Date(Date.now() + 30 * 86_400_000).toISOString();
    else if (p.name === 'date') out.date = fmt.localIsoDate();
  }
  return out;
}
